// TanStack Query hooks over the typed client. Retries skip auth/permission errors. Every org-scoped
// key carries the org ID, so one business's cache never answers for another.
import {
  MutationCache,
  QueryCache,
  QueryClient,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import { api, ApiError } from "./client";

const isPaused = (err: unknown) => err instanceof ApiError && err.code === "dependency.paused";
// D-067: a console switch turned off while the admin is open answers 503 dependency.paused; re-read
// the shop's entitlements at once so the page shows "paused" (not an empty list) without a reload.
const onPaused = (err: unknown) => {
  // SECURITY §2.1: a session that has not passed the user's authenticator yet is sent to that step.
  if (
    err instanceof ApiError &&
    err.code === "auth.mfa_required" &&
    !location.pathname.startsWith("/sign-in")
  ) {
    location.assign(`/sign-in/mfa?next=${encodeURIComponent(location.pathname + location.search)}`);
    return;
  }
  if (isPaused(err))
    void queryClient.invalidateQueries({
      predicate: (q) => q.queryKey[0] === "org" && q.queryKey[2] === "entitlements",
    });
};

export const queryClient: QueryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onPaused }),
  mutationCache: new MutationCache({ onError: onPaused }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (count, err) =>
        !(err instanceof ApiError && err.status >= 400 && err.status < 500) &&
        !isPaused(err) &&
        count < 2,
    },
  },
});

export const keys = {
  me: ["me"] as const,
  sessions: ["auth", "sessions"] as const,
  mfa: ["auth", "mfa"] as const,
  legal: ["legal"] as const,
  members: (orgId: string) => ["org", orgId, "members"] as const,
  invitations: (orgId: string) => ["org", orgId, "invitations"] as const,
  roles: (orgId: string) => ["org", orgId, "roles"] as const,
  audit: (orgId: string) => ["org", orgId, "audit"] as const,
  entitlements: (orgId: string) => ["org", orgId, "entitlements"] as const,
  billing: (orgId: string) => ["org", orgId, "billing"] as const,
};

export const useMe = () => useQuery({ queryKey: keys.me, queryFn: ({ signal }) => api.me(signal) });
export const useLegal = () => useQuery({ queryKey: keys.legal, queryFn: () => api.legal() });
export const useSessions = () =>
  useQuery({ queryKey: keys.sessions, queryFn: () => api.sessions() });
export const useMfaFactors = () =>
  useQuery({ queryKey: keys.mfa, queryFn: () => api.mfaFactors() });

export const useMembers = (orgId: string, enabled = true) =>
  useQuery({ queryKey: keys.members(orgId), queryFn: () => api.members(orgId), enabled });
export const useInvitations = (orgId: string, enabled = true) =>
  useQuery({ queryKey: keys.invitations(orgId), queryFn: () => api.invitations(orgId), enabled });
export const useRoles = (orgId: string, enabled = true) =>
  useQuery({ queryKey: keys.roles(orgId), queryFn: () => api.roles(orgId), enabled });
export const useEntitlements = (orgId: string, enabled = true) =>
  useQuery({
    queryKey: keys.entitlements(orgId),
    queryFn: () => api.entitlements(orgId),
    enabled,
  });
export const useBilling = (orgId: string, enabled = true) =>
  useQuery({ queryKey: keys.billing(orgId), queryFn: () => api.billing(orgId), enabled });

export const AUDIT_PAGE = 50;
/** AUDIT-001 keyset pages, newest first. */
export const useAudit = (orgId: string, enabled = true) =>
  useInfiniteQuery({
    queryKey: keys.audit(orgId),
    queryFn: ({ pageParam }) => api.audit(orgId, { cursor: pageParam, limit: AUDIT_PAGE }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled,
  });
